import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Card,
  Input,
  Select,
  Modal,
  Tag,
  Tooltip,
  Typography,
  Segmented,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  AppleOutlined,
  AndroidOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchTemplates,
  deleteTemplate,
  selectTemplates,
  selectPushLoading,
} from 'store/slices/push.slice';
import {
  fetchApplications,
  selectApplications,
} from 'store/slices/applications.slice';
import { usePermissions } from 'hooks/usePermissions';
import { PushTemplate } from 'types/push_management.types';
import { formatters } from 'utils/formatters';
import BulkCreateModal from './BulkCreateModal';
import { notify } from 'utils/helpers';
import { ROUTES, generatePath } from 'config/routes';
import { asString } from 'utils/resolve';
import '../../styles/Common.css';

const { Search } = Input;
const { confirm } = Modal;
const { Text: AntText } = Typography;
type DeviceType = 'ios' | 'android';

interface PushPreviewProps {
  title: string;
  body: string;
  imageUrl: string;
  appName: string;
}

const IOSPreview: React.FC<PushPreviewProps> = ({ title, body, imageUrl, appName }) => (
  <div
    style={{
      width: 340,
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
    }}
  >
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px 8px',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, color: '#8e8e93', fontWeight: 500, flex: 1 }}>
          {appName || 'App Name'}
        </span>
        <span style={{ fontSize: 12, color: '#8e8e93' }}>сейчас</span>
      </div>
      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', lineHeight: 1.3, marginBottom: 2 }}>
          {title || 'Заголовок'}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#3a3a3c',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {body || 'Текст сообщения'}
        </div>
      </div>
      {imageUrl && (
        <div style={{ padding: '0 12px 12px' }}>
          <img
            src={imageUrl}
            alt="Push image"
            style={{ width: '100%', borderRadius: 12, objectFit: 'cover', maxHeight: 180 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  </div>
);

const AndroidPreview: React.FC<PushPreviewProps> = ({ title, body, imageUrl, appName }) => (
  <div
    style={{
      width: 340,
      margin: '0 auto',
      fontFamily: '"Roboto", "Google Sans", sans-serif',
    }}
  >
    <div
      style={{
        background: '#ffffff',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 4px', gap: 8 }}>
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#4285f4',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 12, color: '#70757a', fontWeight: 400, flex: 1 }}>
          {appName || 'App Name'}
        </span>
        <span style={{ fontSize: 11, color: '#70757a' }}>сейчас</span>
        <span style={{ fontSize: 14, color: '#70757a', marginLeft: 4 }}>▾</span>
      </div>
      <div style={{ padding: '6px 16px 14px', display: 'flex', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#202124', lineHeight: 1.3, marginBottom: 2 }}>
            {title || 'Заголовок'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: '#5f6368',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: imageUrl ? 2 : 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {body || 'Текст сообщения'}
          </div>
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Thumbnail"
            style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      {imageUrl && (
        <div style={{ padding: '0 16px 12px' }}>
          <img
            src={imageUrl}
            alt="Push image"
            style={{ width: '100%', borderRadius: 12, objectFit: 'cover', maxHeight: 180 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  </div>
);

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();
  const templates = useAppSelector(selectTemplates);
  const applications = useAppSelector(selectApplications);
  const isLoading = useAppSelector(selectPushLoading);
  const [selectedAppId, setSelectedAppId] = useState<number | undefined>();
  const [searchText, setSearchText] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<PushTemplate | null>(null);
  const [previewDevice, setPreviewDevice] = useState<DeviceType>('ios');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchApplications({ limit: 100, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedAppId) {
      loadTemplates();
    }
  }, [selectedAppId]);

  const loadTemplates = () => {
    if (selectedAppId) {
      dispatch(fetchTemplates(selectedAppId));
    }
  };

  const handleDelete = (template: PushTemplate) => {
    confirm({
      title: 'Удаление шаблона',
      content: `Вы уверены, что хотите удалить шаблон "${template.name}"?`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteTemplate(template.id)).unwrap();
          notify.success('Шаблон успешно удален');
          loadTemplates();
        } catch (error: any) {
          notify.error(error.message || 'Не удалось удалить шаблон');
        }
      },
    });
  };

  const getAppName = (appId: number): string => {
    return applications.find((app) => app.id === appId)?.name || '';
  };

  const columns: ColumnsType<PushTemplate> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => {
        const searchValue = value.toString().toLowerCase();
        return (
          record.name.toLowerCase().includes(searchValue) ||
          (record.description?.toLowerCase().includes(searchValue) ?? false)
        );
      },
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <AntText type="secondary" style={{ fontSize: 12 }}>
              {formatters.truncate(record.description, 60)}
            </AntText>
          )}
        </div>
      ),
    },
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title: string) => (
        <AntText style={{ fontSize: 13 }}>{formatters.truncate(title, 40)}</AntText>
      ),
    },
    {
      title: 'Сообщение',
      dataIndex: 'body',
      key: 'body',
      render: (body: string) => (
        <AntText type="secondary" style={{ fontSize: 12 }}>
          {formatters.truncate(body, 60)}
        </AntText>
      ),
    },
    {
      title: 'Изображение',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 100,
      align: 'center',
      render: (url?: any) => {
        const resolved = asString(url);
        return resolved ? (
          <Tag color="blue">Есть</Tag>
        ) : (
          <Tag color="default">Нет</Tag>
        );
      },
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => formatters.date(date),
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setPreviewTemplate(record)}
            />
          </Tooltip>
          {permissions.canManagePush && (
            <>
              <Tooltip title="Редактировать">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(generatePath(ROUTES.TEMPLATE_EDIT, { id: record.id }))
                  }
                />
              </Tooltip>
              <Tooltip title="Удалить">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <div className="page-header-content">
            <h1>
              <FileTextOutlined /> Шаблоны Push-уведомлений
            </h1>
          </div>
          <div className="page-header-actions">
            <Select
              placeholder="Выберите приложение"
              style={{ width: 250 }}
              onChange={setSelectedAppId}
              value={selectedAppId}
              options={applications.map((app) => ({
                label: app.name,
                value: app.id,
              }))}
            />
            <Search
              placeholder="Поиск шаблонов..."
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
              onChange={(e) => !e.target.value && setSearchText('')}
            />
            {permissions.canManagePush && selectedAppId && (
              <>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => setBulkModalOpen(true)}
                >
                  Массовое создание
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(`${ROUTES.TEMPLATES}/create`)}
                >
                  Создать шаблон
                </Button>
              </>
            )}
          </div>
        </div>

        {!selectedAppId ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <FileTextOutlined style={{ fontSize: 48, color: '#999' }} />
            <div style={{ marginTop: 16, color: '#999' }}>
              Выберите приложение для просмотра шаблонов
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={templates}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 20,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} из ${total} записей`,
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
            <span>Предпросмотр: {previewTemplate?.name}</span>
            <Segmented
              value={previewDevice}
              onChange={(val) => setPreviewDevice(val as DeviceType)}
              options={[
                { label: <span><AppleOutlined /> iOS</span>, value: 'ios' },
                { label: <span><AndroidOutlined /> Android</span>, value: 'android' },
              ]}
              size="small"
            />
          </div>
        }
        open={!!previewTemplate}
        onCancel={() => setPreviewTemplate(null)}
        footer={null}
        width={480}
        centered
      >
        {previewTemplate && (
          <div>
            <div
              style={{
                borderRadius: previewDevice === 'ios' ? 40 : 24,
                border: `3px solid ${previewDevice === 'ios' ? '#1d1d1f' : '#3c4043'}`,
                padding: '48px 12px 36px',
                background:
                  previewDevice === 'ios'
                    ? 'linear-gradient(180deg, #1c1c1e 0%, #2c2c2e 100%)'
                    : 'linear-gradient(180deg, #202124 0%, #303134 100%)',
                position: 'relative',
                overflow: 'hidden',
                minHeight: 400,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 24,
                }}
              >
                {previewDevice === 'ios' ? (
                  <div style={{ width: 120, height: 28, background: '#000', borderRadius: 14 }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#9aa0a6' }}>12:00</div>
                )}
              </div>
              {previewDevice === 'ios' && (
                <div style={{ textAlign: 'center', marginBottom: 32, marginTop: 8 }}>
                  <div
                    style={{
                      fontSize: 56,
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: '-apple-system, "SF Pro Display", sans-serif',
                      letterSpacing: 2,
                      lineHeight: 1,
                    }}
                  >
                    12:00
                  </div>
                  <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                    Понедельник, 2 марта
                  </div>
                </div>
              )}

              {previewDevice === 'android' && (
                <div style={{ textAlign: 'center', marginBottom: 24, marginTop: 8 }}>
                  <div
                    style={{
                      fontSize: 48,
                      fontWeight: 400,
                      color: '#e8eaed',
                      fontFamily: '"Google Sans", Roboto, sans-serif',
                      lineHeight: 1,
                    }}
                  >
                    12:00
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(232,234,237,0.7)', marginTop: 4 }}>
                    Пн, 2 мар.
                  </div>
                </div>
              )}
              <div style={{ padding: '0 4px' }}>
                {previewDevice === 'ios' ? (
                  <IOSPreview
                    title={previewTemplate.title}
                    body={previewTemplate.body}
                    imageUrl={asString(previewTemplate.image_url) || ''}
                    appName={getAppName(previewTemplate.app_id)}
                  />
                ) : (
                  <AndroidPreview
                    title={previewTemplate.title}
                    body={previewTemplate.body}
                    imageUrl={asString(previewTemplate.image_url) || ''}
                    appName={getAppName(previewTemplate.app_id)}
                  />
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: previewDevice === 'ios' ? 134 : 100,
                  height: 5,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.3)',
                }}
              />
            </div>
            <div style={{ marginTop: 20, padding: '0 4px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <AntText type="secondary" style={{ fontSize: 12 }}>Название шаблона</AntText>
                  <div style={{ fontWeight: 500 }}>{previewTemplate.name}</div>
                </div>
                {previewTemplate.description && (
                  <div>
                    <AntText type="secondary" style={{ fontSize: 12 }}>Описание</AntText>
                    <div>{previewTemplate.description}</div>
                  </div>
                )}
                {asString(previewTemplate.image_url) && (
                  <div>
                    <AntText type="secondary" style={{ fontSize: 12 }}>URL изображения</AntText>
                    <div style={{ wordBreak: 'break-all', fontSize: 13 }}>
                      {asString(previewTemplate.image_url)}
                    </div>
                  </div>
                )}
                {previewTemplate.custom_data && Object.keys(previewTemplate.custom_data).length > 0 && (
                  <div>
                    <AntText type="secondary" style={{ fontSize: 12 }}>Custom Data</AntText>
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: 8,
                        borderRadius: 6,
                        fontSize: 12,
                        margin: '4px 0 0',
                        maxHeight: 120,
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(previewTemplate.custom_data, null, 2)}
                    </pre>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 24 }}>
                  <div>
                    <AntText type="secondary" style={{ fontSize: 12 }}>Создан</AntText>
                    <div>{formatters.date(previewTemplate.created_at)}</div>
                  </div>
                  <div>
                    <AntText type="secondary" style={{ fontSize: 12 }}>Обновлен</AntText>
                    <div>{formatters.date(previewTemplate.updated_at)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
      {selectedAppId && (
        <BulkCreateModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          appId={selectedAppId}
          onComplete={loadTemplates}
        />
      )}
    </div>
  );
};

export default TemplateList;